import React, { useState, useEffect } from "react";
import styled from "styled-components";
import useStore from "../store.js";

const MIN_FLOATING_VIEWPORT_HEIGHT = 781;
const MIN_DESKTOP_FLOATING_VIEWPORT_HEIGHT = 900;
const MIN_TABLET_FLOATING_VIEWPORT_HEIGHT = 960;

const getVisibleViewportHeight = () => {
  const visualHeight = window.visualViewport?.height;
  return Number.isFinite(visualHeight) ? visualHeight : window.innerHeight;
};

const hasFloatingRoom = () => {
  const minimum = window.innerWidth >= 1440 ? MIN_DESKTOP_FLOATING_VIEWPORT_HEIGHT
    : window.innerWidth >= 600 ? MIN_TABLET_FLOATING_VIEWPORT_HEIGHT
      : MIN_FLOATING_VIEWPORT_HEIGHT;
  const height = getVisibleViewportHeight();
  if (height < minimum) return false;
  const intro = document.querySelector('[data-floating-intro="true"]');
  // Reserve 48px for the button, 27px for its hint and 32px below them.
  // Live keyword rows can move the input notices down without a viewport resize.
  return window.innerWidth >= 1440 || !intro ||
    intro.getBoundingClientRect().bottom + window.scrollY + 8 <= height - 107;
};

// 온보딩 플로팅 버튼 컴포넌트
const FloatingButton = ({ scrollToRef, inline = false, label = "Lawlow가 신뢰받는 이유를 알고 싶다면?" }) => {
  const [isVisible, setIsVisible] = useState(true); // 버튼의 보임/숨김 상태 관리
  const [hasVerticalRoom, setHasVerticalRoom] = useState(
    hasFloatingRoom
  );
  const isChatMode = useStore((state) => state.isChatMode);

  // 스크롤 이벤트 리스너
  const handleScroll = () => {
    if (window.scrollY > 0) {
      setIsVisible(false); // 스크롤하면 버튼 숨기기
    } else {
      setIsVisible(true); // 맨 위로 가면 버튼 보이기
    }
  };

  // 버튼 클릭 시 스크롤 기능
  const handleButtonClick = () => {
    if (scrollToRef && scrollToRef.current) {
      scrollToRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll); // 스크롤 이벤트 추가
    return () => {
      window.removeEventListener("scroll", handleScroll); // 컴포넌트 언마운트 시 이벤트 리무브
    };
  }, []);

  useEffect(() => {
    const updateVerticalRoom = () => {
      setHasVerticalRoom(hasFloatingRoom());
    };

    updateVerticalRoom();
    const intro = document.querySelector('[data-floating-intro="true"]');
    const observer = intro && window.ResizeObserver ? new ResizeObserver(updateVerticalRoom) : null;
    if (observer) observer.observe(intro);
    window.addEventListener("resize", updateVerticalRoom);
    window.visualViewport?.addEventListener("resize", updateVerticalRoom);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateVerticalRoom);
      window.visualViewport?.removeEventListener("resize", updateVerticalRoom);
    };
  }, [isChatMode]);

  if (isChatMode || (!inline && !hasVerticalRoom)) {
    return null;
  }

  return (
    <FloatingButtonContainer $isVisible={inline || isVisible} $inline={inline}>
      <Button data-landing-floating="true" data-behavior="landing-scroll" onClick={handleButtonClick}>
        <ArrowSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 14">
          <path
            d="M4.5 0.75V13.25M4.5 13.25L1 9.75M4.5 13.25L8 9.75"
            stroke="black"
          />
        </ArrowSvg>
        <ButtonText>{label}</ButtonText>
        <ArrowSvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 9 14">
          <path
            d="M4.5 0.75V13.25M4.5 13.25L1 9.75M4.5 13.25L8 9.75"
            stroke="black"
          />
        </ArrowSvg>
      </Button>
      {!inline && isVisible && <ScrollText>아래로 스크롤하세요!</ScrollText>}
    </FloatingButtonContainer>
  );
};

// 스타일링
const FloatingButtonContainer = styled.div`
  display: ${({ $isVisible }) => ($isVisible ? "block" : "none")};
  position: fixed;
  bottom: 120px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 999;

  @media (min-width: 1440px) {
    position: absolute;
    top: 815px;
    bottom: auto;
  }

  @media (max-width: 1439px) {
    bottom: 32px;
  }

  // 모바일에서 플로팅버튼 뒤에 blur 배경 추가
  @media (max-width: 579px) {
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 312px;
      height: 48px;
      border-radius: 24px;
      background: #FFF;
      filter: blur(25px);
      z-index: -1; // 버튼 뒤에 배치
    }
  }
  ${({ $inline }) => $inline && `
    && { position: relative; top: auto; bottom: auto; left: auto; transform: none; z-index: auto; padding: 24px; text-align: center; }
    &::before { display: none; }
  `}
`;

const Button = styled.button`
  display: inline-flex;
  height: 48px;
  padding: 18px 24px;
  align-items: center;
  gap: 11px;
  flex-shrink: 0;
  border-radius: 24px;
  border: 0.5px solid var(--main-gradient, #1ae276);
  background: #fff;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.1);
  cursor: pointer;

  /* 그라데이션 */
  // gradientBorder 안 쓰고 일부러 custom함 - deg, %
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit; /* 부모의 border-radius를 상속받음 */
    padding: 0.02rem; /* 테두리 두께 */
    background: linear-gradient(
      90deg,
      #1ae276 0%,
      #1ae276 50%,
      #0057ff 50%,
      #0057ff 100%
    );
    -webkit-mask: linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: destination-out;
    mask-composite: exclude;
    pointer-events: none; /* 클릭 이벤트를 받지 않도록 설정 */
  }

  @media (max-width: 579px) {
    width: 314px;
  }
`;

const ArrowSvg = styled.svg`
  width: 7px;
  height: 12.5px;
  stroke-width: 1px;
  stroke: #000;
`;

const ButtonText = styled.span`
  color: #000;
  text-align: center;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
`;

const ScrollText = styled.p`
  text-align: center;
  color: #999;
  font-family: Pretendard;
  font-size: 14px;
  line-height: 17px;
  margin: 10px 0 0;
`;

export default FloatingButton;
